import instance from "../../api/Api";
import { configs } from "../../configs/configs";

/**
 * Generates chart data by requesting the backend API with provided parameters.
 *
 * @async
 * @function generateChartData
 * @param {Object} params - The parameters for the chart data request.
 * @param {string} params.qouteCoin - The base coin (will be uppercased).
 * @param {string} params.pair - The quote coin (will be uppercased).
 * @param {string|number} params.timeframe - The chart timeframe.
 * @param {string} params.chartIndicator - The indicator to use for the chart.
 * @param {string} params.exchange - The name of the exchange.
 * @param {Object} params.chartData - Chart data, including timestamps and prices.
 * @param {Array<number|string>} params.chartData.timestamps - Array of timestamps.
 * @param {Array<number>} params.chartData.prices - Array of prices.
 * @param {string} [params.lastMatchedDate] - The last matched date for data filtering (optional).
 * @param {string} [params.status] - The status filter for the chart data (optional).
 * @returns {Promise<Object>} The chart data returned from the backend.
 */
export const generateChartData = async (params) => {
  const {
    qouteCoin,
    pair,
    timeframe,
    chartIndicator,
    exchange,
    chartData,
    lastMatchedDate,
    status,
  } = params;

  const rawTimeframe = String(timeframe || "").trim();
  const normalizedTimeframe = rawTimeframe.toLowerCase();
  const normalizedResolution = normalizeResolution(normalizedTimeframe);

  // Legacy chart-url service can fallback to 1m for minute keys like "15m".
  // Send minute-based timeframes as plain minutes too (e.g. 15m -> 15).
  const minuteKeyMatch = normalizedTimeframe.match(/^(\d+)m$/);
  const legacyTimeframeValue = minuteKeyMatch ? minuteKeyMatch[1] : rawTimeframe;

  const queryParams = {
    base_coin: qouteCoin.toUpperCase(),
    quote_coin: pair.toUpperCase(),
    timeframe: legacyTimeframeValue || undefined,
    timeframe_key: rawTimeframe || undefined,
    resolution: normalizedResolution,
    interval: normalizedResolution,
    indicator: chartIndicator,
    exchange,
    d1: chartData?.timestamps[0],
    p1: chartData?.prices[0],
    d2: chartData?.timestamps[1],
    p2: chartData?.prices[1],
    d3: chartData?.timestamps[2],
    p3: chartData?.prices[2],
  };

  if (lastMatchedDate) {
    queryParams.last_match_date = lastMatchedDate;
  }

  if (status) {
    queryParams.status = status;
  }

  const response = await instance.get(configs.chart_url, {
    params: queryParams,
  });
  return response.data;
};

const TIMEFRAME_TO_RESOLUTION = {
  "1m": "1",
  "3m": "3",
  "5m": "5",
  "15m": "15",
  "30m": "30",
  "1h": "60",
  "2h": "120",
  "4h": "240",
  "6h": "360",
  "8h": "480",
  "12h": "720",
  "1d": "D",
  d: "D",
  "1w": "W",
  w: "W",
  "1M": "M",
  m: "M",
};

export const normalizeResolution = (timeframe) => {
  const key = String(timeframe || "").trim();
  if (!key) return "60";
  return TIMEFRAME_TO_RESOLUTION[key] || TIMEFRAME_TO_RESOLUTION[key.toLowerCase()] || key;
};

export const getChartDirectEmbedFlag = async () => {
  const { data } = await instance.get(configs.chart_direct_embed_flag_url);
  return data;
};

export const getGlobalMarketMetrics = async () => {
  const { data } = await instance.get(configs.chart_global_market_metrics_url);
  return data;
};

export const getChartRenderPayload = async (payload) => {
  const { data } = await instance.post(configs.chart_render_payload_url, payload);
  return data;
};

export const saveChartPersistence = async (payload) => {
  const { data } = await instance.post(configs.chart_persistence_save_url, payload);
  return data;
};

export const loadChartPersistence = async (params) => {
  const { data } = await instance.get(configs.chart_persistence_load_url, { params });
  return data;
};
